import React, { useEffect, useMemo } from "react";

import "./index.scss";
import useActions from "../../../../hooks/service-actions.hook";
import { ToolEffectActions } from "../../../../common/services/security-stack-view/tool.service";
import { useEntity } from "../../../../hooks/use-entity.hook";
import { GetToolsInMyStackResponse } from "../../../../common/services/security-stack-view/dto/get-tools-in-my-stack.dto";
import { Entities } from "../../../../common/store/entities/entities.enum";
import { Flaggers } from "../../../../common/store/flaggers/flaggers.enum";
import { FlaggersMethods } from "../../../../common/store/flaggers/flaggers.methods";
import { ToolsFromBE } from "../../../../common/types/tool.types";
import Suspense from "../../../Common/suspense.component";
import { Loaders } from "../../../../common/store/loaders/loaders.enum";
import cn from "../../../../Utils/class-names.utils";
import PlusIcon from "../../../icons/plus.icon";
import ImageLoader from "../../../Common/ImageLoader";
import integrationUtils from "../../../../Utils/integration.utils";
import OrderSummary from "../../../Common/loader";

interface YourStackProps {
  smallVersion?: boolean;
  showAddButton?: boolean;
}

const YourStack = ({ smallVersion, showAddButton = true }: YourStackProps) => {
  const { getToolsInMyStack } = useActions(ToolEffectActions);
  const { entityData: toolsInStackData } = useEntity<GetToolsInMyStackResponse>({ entityType: Entities.ToolInStack });
  const { setEntityData: setAddToolsModal } = useEntity({ entityType: Flaggers.AddToolsModalFlagger });
  const { setEntityData: setToolModal } = useEntity({ entityType: Flaggers.UpdateToolModalFlagger });
  const { setEntityData: setPracticeModal } = useEntity({ entityType: Flaggers.UpdatePracticeModalFlagger });

  const toolsList = toolsInStackData?.SecurityStack_List || [];

  const filteredToolsList = useMemo(() => {
    return toolsList.filter((tool) => !tool?.IsPractice);
  }, [toolsList]);

  useEffect(() => {
    getToolsInMyStack();
  }, []);

  const handleAddToolsClick = () => {
    setAddToolsModal({ show: true }, FlaggersMethods.UpdateFlagger);
  };

  const handleToolClick = (tool: ToolsFromBE) => {
    if (tool?.IsPractice) {
      setPracticeModal({
        show: true,
        practice: {
          IntegrationId: tool.IntegrationId,
          obj_IntegrationId: tool.IntegrationId,
          ToolName: tool.IntegrationName,
          ToolIcon: tool.ToolIcon,
        },
      }, FlaggersMethods.UpdateFlagger);
      return;
    }
    setToolModal({
      show: true,
      tool: {
        obj_IntegrationId: tool.IntegrationId,
        ToolName: tool.IntegrationName,
        ToolIcon: tool.ToolIcon,
      },
    }, FlaggersMethods.UpdateFlagger);
  };

  return (
    <Suspense loaders={Loaders.GetToolsInMyStackLoader} fallback={<SectionLoader />} forceShow={!!toolsInStackData?.SecurityStack_List}>
      {!filteredToolsList?.length ? (
        <EmptyToolsList handleAddToolsClick={handleAddToolsClick} showAddButton={showAddButton} />
      ) : (
        <div className={cn("stack-tools-grid-container")}>
          <div className={cn("stack-tools-grid", { "small-version": smallVersion })}>
            {filteredToolsList?.map((tool: ToolsFromBE) => (
              <ToolsListItem key={tool.IntegrationId} tool={tool} onToolClick={handleToolClick} />
            ))}

            {showAddButton && (
              <div className="stack-tools-grid__add-button">
              <button
                className="stack-tools-grid__add-button__inner"
                aria-label="Add new avatar"
                type="button"
                onClick={handleAddToolsClick}
              >
                <PlusIcon fill="#BBBEC9" width={20} height={20} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </Suspense>
  );
};

export default YourStack;

const ToolsListItem = ({ tool, onToolClick }: { tool: ToolsFromBE; onToolClick: (tool: ToolsFromBE) => void }) => {
  const imagePath = integrationUtils.getImagePath(tool.IntegrationId, tool.ToolIcon);
  return (
    <div
      className="stack-tools-grid__item pointer"
      title={tool.IntegrationName}
      onClick={() => onToolClick(tool)}
    >
      <ImageLoader
        alt=""
        width={16}
        height={16}
        className="stack-tools-grid__item__image"
        src={imagePath}
        noImageComponent={(
          <img
            alt=""
            width={16}
            height={16}
            className="stack-tools-grid__item__image"
            src={"/images/attack-surface/generic2-dark.svg"}
          />
        )}
      />
    </div>
  )
}

interface EmptyToolsListProps {
  handleAddToolsClick: () => void;
  showAddButton: boolean;
}

const EmptyToolsList = ({ handleAddToolsClick, showAddButton }: EmptyToolsListProps) => {
  return (
    <div className="stack-tools-grid-empty">
      <div
        className="stack-tools-grid-empty__content"
        role="main"
      >
        <img
          className="stack-tools-grid-empty__icon"
          alt="Toolset icon"
          src={"/images/plug.icon.png"}
        />

        <h1 className="stack-tools-grid-empty__title m-0">
          Start Creating your Toolset
        </h1>

        <p className="stack-tools-grid-empty__description m-0">
          Add your tools, track product costs and license usage in one place.
        </p>

        {showAddButton && (
          <button
            className="stack-tools-grid-empty__button"
            onClick={handleAddToolsClick}
            type="button"
            aria-label="Add Tools"
          >
            <span className="stack-tools-grid-empty__button__content">
              <span className="stack-tools-grid-empty__button__text">
                Add Tools
              </span>
            </span>
          </button>
        )}
      </div>
    </div>
  )
}

const SectionLoader = () => {
  return (
    <div className="stack-tools-grid-empty">
      <div className="stack-tools-grid-empty__content">
        <OrderSummary />
      </div>
    </div>
  )
}